import type { ComponentConfig } from '@measured/puck';
import { Hammer, Handshake, ShieldCheck, type LucideIcon } from 'lucide-react';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { StaggerGrid, StaggerItem } from '../motion/StaggerGrid';
import { cn } from '../../lib/cn';

interface PolesTriptyqueProps {
  eyebrow?: string;
  title?: string;
  description?: string;
}

interface PoleEntry {
  id: 'travaux' | 'courtage' | 'mandataire-cee';
  icon: LucideIcon;
  label: string;
  promise: string;
  badge?: string;
  tone: 'brand' | 'neutral' | 'accent';
}

const POLES: PoleEntry[] = [
  {
    id: 'travaux',
    icon: Hammer,
    label: 'Travaux & rénovation',
    promise:
      "Vos travaux réalisés par notre réseau d'artisans RGE partenaires — aides déjà déduites du devis.",
    tone: 'brand',
  },
  {
    id: 'courtage',
    icon: Handshake,
    label: 'Courtage énergie',
    promise:
      "Comparateur indépendant des fournisseurs d'énergie — contrats négociés, économies sur vos coûts d'acheminement et vos taxes d'énergie selon votre secteur.",
    tone: 'neutral',
  },
  {
    id: 'mandataire-cee',
    icon: ShieldCheck,
    label: 'Mandataire CEE',
    promise: 'On encaisse, on dépose et on défend vos dossiers CEE au registre officiel.',
    badge: 'Mandataire CEE',
    tone: 'accent',
  },
];

// V4 palette restreinte : brand (vert) / neutral (ink+stone) / accent (lime — phare)
const TONES = {
  brand: { iconBg: 'bg-brand-50', iconText: 'text-brand-700', dot: 'bg-brand-500' },
  neutral: { iconBg: 'bg-stone-100', iconText: 'text-ink', dot: 'bg-stone-600' },
  accent: { iconBg: 'bg-accent-50', iconText: 'text-accent-700', dot: 'bg-accent-500' },
} as const;

export function PolesTriptyque({
  eyebrow = 'Notre métier',
  title = 'Trois pôles. Un seul interlocuteur. Zéro coordination à gérer.',
  description = 'On réalise vos travaux, on négocie vos contrats gaz et électricité, on monte les dossiers de primes des artisans. Un seul contact, on coordonne tout.',
}: PolesTriptyqueProps) {
  return (
    <section className="bg-paper py-16 md:py-20">
      <Container>
        <Reveal>
          <div className="mx-auto mb-12 max-w-xl text-center">
            <p className="eyebrow justify-center">{eyebrow}</p>
            <h2 className="h-section mt-4">{title}</h2>
            <p className="mt-4 text-base leading-relaxed text-stone-600">{description}</p>
          </div>
        </Reveal>

        <StaggerGrid className="mx-auto grid max-w-5xl gap-4 md:grid-cols-3">
          {POLES.map((pole) => {
            const Icon = pole.icon;
            const tone = TONES[pole.tone];
            return (
              <StaggerItem key={pole.id}>
                <article className="group relative flex h-full flex-col gap-5 rounded-2xl border border-stone-200/70 bg-surface p-6 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-floating">
                  {pole.badge && (
                    <span className="absolute right-5 top-5 inline-flex items-center gap-1 rounded-full bg-ink px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-ivory-50">
                      <span
                        className={cn('h-1 w-1 animate-glow-pulse rounded-full', 'bg-accent-400')}
                      />
                      {pole.badge}
                    </span>
                  )}
                  <div
                    className={cn(
                      'inline-flex h-11 w-11 items-center justify-center rounded-xl shadow-soft',
                      tone.iconBg,
                      tone.iconText,
                    )}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.6} />
                  </div>
                  <div>
                    <p className="eyebrow">{pole.label}</p>
                    <h3 className="mt-1.5 font-display text-lg font-semibold leading-tight text-ink">
                      {pole.promise}
                    </h3>
                  </div>
                </article>
              </StaggerItem>
            );
          })}
        </StaggerGrid>

        <Reveal>
          <p className="mx-auto mt-10 max-w-md text-center text-xs text-stone-500">
            Détail dans nos{' '}
            <a
              href="/legal/cgv-courtier-energie"
              className="font-semibold text-brand-700 underline-offset-4 hover:underline"
            >
              CGV et mandats
            </a>
            .
          </p>
        </Reveal>
      </Container>
    </section>
  );
}

export const PolesTriptyqueConfig: ComponentConfig<PolesTriptyqueProps> = {
  fields: {
    eyebrow: { type: 'text' },
    title: { type: 'textarea' },
    description: { type: 'textarea' },
  },
  defaultProps: {
    eyebrow: 'Notre métier',
    title: 'Trois pôles. Un seul interlocuteur. Zéro coordination à gérer.',
    description:
      'On réalise vos travaux, on négocie vos contrats gaz et électricité, on monte les dossiers de primes des artisans. Un seul contact, on coordonne tout.',
  },
  render: PolesTriptyque,
};
